// src/client/components/profile-manager/detail-view/ProfileDetailHeader.tsx

import { ArrowLeft, ChevronLeft, ChevronRight, Loader2 } from 'lucide-react';
import { Button } from '@client/components/ui/button';
import type { ProfileWithRelations } from '@shared/types';

interface ProfileDetailHeaderProps {
  profile: ProfileWithRelations;
  onBack: () => void;
  // ✅ Navigation Props
  onPrevious?: () => void;
  onNext?: () => void;
  hasPrevious?: boolean;
  hasNext?: boolean;
  position?: number;
  total?: number;
  isNavigationLoading?: boolean;
}

export default function ProfileDetailHeader({
  profile,
  onBack,
  onPrevious,
  onNext,
  hasPrevious = false,
  hasNext = false,
  position,
  total,
  isNavigationLoading = false
}: ProfileDetailHeaderProps) {
  return (
    <div className="flex items-center gap-4 pb-4 border-b">
      {/* Back Button */}
      <Button
        variant="ghost"
        size="sm"
        onClick={onBack}
        className="flex items-center gap-2"
      >
        <ArrowLeft className="h-4 w-4" />
        Back
      </Button>
      
      {/* ✅ Navigation Controls - FIXED ORDER */}
      {isNavigationLoading ? (
        <div className="flex items-center gap-2 border-l pl-4">
          <Button variant="outline" size="sm" disabled>
            <Loader2 className="h-4 w-4 animate-spin" />
          </Button>
          <span className="text-sm text-gray-400">Loading...</span>
          <Button variant="outline" size="sm" disabled>
            <Loader2 className="h-4 w-4 animate-spin" />
          </Button>
        </div>
      ) : (hasPrevious || hasNext) && (
        <div className="flex items-center gap-2 border-l pl-4">
          {/* ✅ PREVIOUS (Zurück) - LINKS */}
          <Button
            variant="outline"
            size="sm"
            onClick={onPrevious}
            disabled={!hasPrevious}
            title="Previous profile (←)"
          >
            <ChevronLeft className="h-4 w-4" />
          </Button>
          
          {/* Position Counter */}
          {position && total && (
            <span className="text-sm text-gray-500 px-2 min-w-[80px] text-center">
              {position} / {total}
            </span>
          )}
          
          {/* ✅ NEXT (Vor) - RECHTS */}
          <Button
            variant="outline"
            size="sm"
            onClick={onNext}
            disabled={!hasNext}
            title="Next profile (→)"
          >
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>
      )}
      
      {/* Profile Title */}
      <div className="flex-1">
        <h1 className="text-2xl font-bold">
          {profile.name || 'Unnamed Profile'}
        </h1>
        <p className="text-sm text-gray-500">
          Profile ID: {profile.id}
          {profile.linkedinId && ` • LinkedIn ID: ${profile.linkedinId}`}
        </p>
      </div>
      
      {/* Status Badges */}
      <div className="flex gap-2">
        {profile.isActive && (
          <span className="px-2 py-1 text-xs bg-green-100 text-green-800 rounded">
            Active
          </span>
        )}
        {profile.isBlocked && (
          <span className="px-2 py-1 text-xs bg-red-100 text-red-800 rounded">
            Blocked
          </span>
        )}
        {profile.isRegistered && (
          <span className="px-2 py-1 text-xs bg-blue-100 text-blue-800 rounded">
            Registered
          </span>
        )}
      </div>
    </div>
  );
}